import { Box, Typography, Chip, Tabs, Tab, IconButton, Badge, Dialog, DialogTitle, DialogContent, DialogActions, Button, LinearProgress, FormControlLabel, Switch, RadioGroup, Radio, FormControl, FormLabel, List, ListItemButton, ListItemText, Alert } from '@mui/material';
import CategoryList from '../components/categories/CategoryList';
import CategoryPickerModal, { PickerCategory } from '../components/categories/CategoryPickerModal';
import ProposalList, { FeedProposal } from '../components/proposals/ProposalList';
import FeedSkeleton from '../components/proposals/FeedSkeleton';
import FeedLoadMoreFooter from '../components/proposals/FeedLoadMoreFooter';
import AppShell from '../components/layout/AppShell';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Groq from "groq-sdk"; // Import Groq
import { Close, FilterList } from '@mui/icons-material'; // Added Close icon
import { Category } from '../types';
import DesignSystem from '../designSystem';
import {
  fetchUserCategories,
  HOME_FEED_PAGE_SIZE,
  getCategoryIconUrl,
  ApiBusiness,
  getBusinessLogoUrl,
  getBusinessCoverUrl,
  fetchFavorites,
  fetchNewsByCreatedAt,
  fetchCategories,
  fetchOnlyUserCategories,
  updateUserCategories,
  fetchUserFolders,
  addBusinessToFolder,
  removeBusinessFromAllFolders,
  ApiFolder,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { isFeatureEnabled } from '../config/featureFlags';
import { getApiLanguage, VISIME_LANGUAGE_CHANGED } from '../config/apiLanguage';
import { useT } from '../i18n/useT';
import { isTestEntityName } from '../utils/testEntityName';
import { displayFolderName } from '../utils/folderDisplayName';
import { areHomeFeedFiltersActive } from './homeFeedFiltersActive';
import { formatDistanceKm } from '../utils/distanceDisplay';
import {
  defaultZurichCoordinates,
  isPlausibleCoordinates,
  loadViewerLocationState,
  resolveHomeViewerLocation,
  resolveViewerCoordinates,
  VIEWER_LOCATION_CHANGED,
} from '../utils/geolocation';

const LOCATION_TIMEOUT_MS = 8000;

const LOCATION_MODE_KEY = 'visime_home_location_mode';
type LocationMode = 'all' | 'fixed' | 'gps';

function readLocationMode(): LocationMode {
  try {
    const v = localStorage.getItem(LOCATION_MODE_KEY);
    if (v === 'fixed' || v === 'gps' || v === 'all') return v;
  } catch {
    /* ignore */
  }
  return 'all';
}

function placeTypeFromMode(mode: LocationMode): number {
  if (mode === 'fixed') return 1;
  if (mode === 'gps') return 2;
  return 3;
}

// Initialize Groq client
const groqApiKey = process.env.REACT_APP_GROQ_API_KEY;
let groq: Groq | null = null;
if (groqApiKey) {
  groq = new Groq({ apiKey: groqApiKey, dangerouslyAllowBrowser: true });
}



// Debounce time in milliseconds
const DEBOUNCE_DELAY = 1500; // Reduced to 1.5 seconds for better feel

export default function HomePage() {
  const navigate = useNavigate();
  const { user, refreshSession } = useAuth();
  const t = useT();
  void isFeatureEnabled('webAiAssistant');
  void isFeatureEnabled('webProposalsFeed');
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [locationMode, setLocationMode] = useState<LocationMode>(readLocationMode);
  const feedFiltersActive = areHomeFeedFiltersActive(favoritesOnly, locationMode);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [pickerBusy, setPickerBusy] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerCategories, setPickerCategories] = useState<PickerCategory[]>([]);
  const [pickerSelectedIds, setPickerSelectedIds] = useState<number[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [businesses, setBusinesses] = useState<ApiBusiness[]>([]);
  const [feedPage, setFeedPage] = useState(1);
  const [feedHasMore, setFeedHasMore] = useState(false);
  const [visibleFeedCount, setVisibleFeedCount] = useState(HOME_FEED_PAGE_SIZE);
  const [isLoadingMoreBusinesses, setIsLoadingMoreBusinesses] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(false);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const feedHasMoreRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const isLoadingBusinessesRef = useRef(false);
  const visibleFeedCountRef = useRef(HOME_FEED_PAGE_SIZE);
  const businessesLenRef = useRef(0);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string | null>(null);
  const [selectedSubsubcategoryId, setSelectedSubsubcategoryId] = useState<string | null>(null);
  // Track deselected subcategory IDs (iOS model: all selected by default, user deselects specific ones)
  const [deselectedSubcategoryIds, setDeselectedSubcategoryIds] = useState<Set<number>>(new Set());
  const [llmFilteredProposalIds, setLlmFilteredProposalIds] = useState<string[] | null>(null);
  const [activeLlmQuery, setActiveLlmQuery] = useState<string | null>(null);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  // Lifted state for the chat input value
  const [chatQuery, setChatQuery] = useState('');
  // Feed/Notifications tab state (matches iOS app)
  const [feedTab, setFeedTab] = useState<'feed' | 'notifications'>('feed');
  const [notificationCount, setNotificationCount] = useState(0);
  const [newsItems, setNewsItems] = useState<Array<Record<string, unknown>>>([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsErr, setNewsErr] = useState<string | null>(null);
  const [langTick, setLangTick] = useState(0);
  const [saveFolderOpen, setSaveFolderOpen] = useState(false);
  const [saveFolders, setSaveFolders] = useState<ApiFolder[]>([]);
  const [saveFolderErr, setSaveFolderErr] = useState<string | null>(null);
  const [saveFolderOk, setSaveFolderOk] = useState<string | null>(null);
  const [saveTargetId, setSaveTargetId] = useState<number | null>(null);
  const [saveActionBusy, setSaveActionBusy] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const locationRef = useRef<{ lat: string; lng: string } | null>(null);

  useEffect(() => {
    const onLang = () => {
      setCategories([]);
      setBusinesses([]);
      setLangTick((t) => t + 1);
    };
    window.addEventListener(VISIME_LANGUAGE_CHANGED, onLang);
    return () => window.removeEventListener(VISIME_LANGUAGE_CHANGED, onLang);
  }, []);

  // Initial load: refresh JWT then fetch categories (matches iOS vcHome refreshFeed)
  useEffect(() => {
    if (!user?.token) return;

    const fetchCategories = async (latParam?: string, lngParam?: string) => {
      const authToken = await refreshSession();
      if (!authToken) return;

      setIsLoadingCategories(true);
      try {
        const resolved = resolveViewerCoordinates(
          latParam != null && lngParam != null ? { lat: latParam, lng: lngParam } : locationRef.current
        );
        const placeType = placeTypeFromMode(locationMode);

        const data = await fetchUserCategories(
          getApiLanguage(),
          authToken,
          resolved.lat,
          resolved.lng,
          placeType,
          undefined,
          { page: 1, limit: HOME_FEED_PAGE_SIZE }
        );
        
        if (data.message && !data.categories) {
          console.warn('API returned error message:', data.message);
          setCategories([]);
          return;
        }
        
        if (!data.categories || !Array.isArray(data.categories)) {
          console.error('Invalid API response structure:', data);
          setCategories([]);
          return;
        }
        
        // Transform backend categories data
        const transformedCategories = data.categories.map((cat: any) => {
          // Prefer `image` (filename under categoryimages/) over `icon` (short slug).
          const iconUrl = cat.image || cat.icon;
          const fullImageUrl = iconUrl ? (getCategoryIconUrl(iconUrl) ?? undefined) : undefined;
          
          const subcategoriesArray = Array.isArray(data.subcategories) ? data.subcategories : [];
          const categorySubcategories = subcategoriesArray
            .filter((sub: any) => sub.category_id === cat.id)
            .map((sub: any) => {
              const subIconUrl = sub.image || sub.icon || iconUrl;
              const subFullImageUrl = subIconUrl ? (getCategoryIconUrl(subIconUrl) ?? undefined) : fullImageUrl;
              
              const subsubcategoriesArray = Array.isArray(data.subsubcategories) ? data.subsubcategories : [];
              const subsubcategories = subsubcategoriesArray
                .filter((subsub: any) => subsub.subcategory_id === sub.id)
                .map((subsub: any) => ({
                  id: subsub.id.toString(),
                  name: subsub.name,
                  imageUrl: subFullImageUrl ?? undefined,
                  categoryId: cat.id.toString(),
                  subcategoryId: sub.id.toString(),
                }));
              
              return {
                id: sub.id.toString(),
                name: sub.name,
                imageUrl: subFullImageUrl ?? undefined,
                categoryId: cat.id.toString(),
                subsubcategories: subsubcategories.length > 0 ? subsubcategories : undefined,
              };
            });
          
          return {
            id: cat.id.toString(),
            name: cat.name,
            imageUrl: fullImageUrl ?? undefined,
            icon: iconUrl,
            color: cat.color,
            favorite: cat.active === 1 || cat.favorite === 1,
            subcategories: categorySubcategories.length > 0 ? categorySubcategories : undefined,
          };
        }) as Category[];

        setCategories(transformedCategories);
        
        // Also set initial businesses if available (first page)
        if (data.businesses && Array.isArray(data.businesses)) {
          setBusinesses(data.businesses);
          businessesLenRef.current = data.businesses.length;
          setFeedPage(1);
          setVisibleFeedCount(HOME_FEED_PAGE_SIZE);
          visibleFeedCountRef.current = HOME_FEED_PAGE_SIZE;
          const more = Boolean(data.has_more);
          setFeedHasMore(more);
          feedHasMoreRef.current = more;
          console.log(`[Initial Load] Loaded ${data.businesses.length} businesses (has_more=${more})`);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    let cancelled = false;

    // Seed immediately so the category strip never waits on GPS/IP.
    // Upgrade when resolve finishes (fixed / GPS / ISP / Zürich).
    const pref = loadViewerLocationState();
    if (pref.mode === 'fixed' && isPlausibleCoordinates(pref.lat, pref.lng)) {
      locationRef.current = { lat: pref.lat, lng: pref.lng };
    } else if (!locationRef.current) {
      locationRef.current = defaultZurichCoordinates();
    }
    void fetchCategories(locationRef.current.lat, locationRef.current.lng);

    void resolveHomeViewerLocation({ timeoutMs: LOCATION_TIMEOUT_MS }).then((resolved) => {
      if (cancelled) return;
      const prev = locationRef.current;
      locationRef.current = resolved.coords;
      // Same ~1km bucket as api/backend cache keys — skip duplicate home feed fetch.
      const sameBucket =
        prev &&
        Number(prev.lat).toFixed(2) === Number(resolved.coords.lat).toFixed(2) &&
        Number(prev.lng).toFixed(2) === Number(resolved.coords.lng).toFixed(2);
      if (!sameBucket) {
        void fetchCategories(resolved.coords.lat, resolved.coords.lng);
      }
    });

    const onViewerLocationChanged = () => {
      void resolveHomeViewerLocation({ timeoutMs: LOCATION_TIMEOUT_MS }).then((resolved) => {
        if (cancelled) return;
        locationRef.current = resolved.coords;
        void fetchCategories(resolved.coords.lat, resolved.coords.lng);
      });
    };
    window.addEventListener(VIEWER_LOCATION_CHANGED, onViewerLocationChanged);

    return () => {
      cancelled = true;
      window.removeEventListener(VIEWER_LOCATION_CHANGED, onViewerLocationChanged);
    };
  }, [user?.token, langTick, locationMode, refreshSession]);

  useEffect(() => {
    if (!user?.token) return;
    setNewsLoading(true);
    setNewsErr(null);
    fetchNewsByCreatedAt(user.token)
      .then((r) => {
        const news = Array.isArray(r.news) ? r.news : [];
        setNewsItems(news);
        setNotificationCount(news.length);
      })
      .catch((e) => {
        setNotificationCount(0);
        setNewsItems([]);
        setNewsErr(e instanceof Error ? e.message : t('homeUpdatesFailed'));
      })
      .finally(() => setNewsLoading(false));
  }, [user?.token, feedTab, t]);

  useEffect(() => {
    if (!manageCategoriesOpen || !user?.token) return;
    let cancelled = false;
    setPickerError(null);
    setPickerLoading(true);
    Promise.all([fetchCategories(getApiLanguage(), user.token), fetchOnlyUserCategories(getApiLanguage(), user.token)])
      .then(([all, only]) => {
        if (cancelled) return;
        const cats = (all.categories || []).map((c) => ({
          id: c.id,
          name: c.name,
          image: c.image,
          icon: (c as { icon?: string }).icon,
        }));
        setPickerCategories(cats);
        // Match iOS: selection comes from getOnlyUserCategories (not favorite flags alone).
        const fromOnly = (only.categories || []).map((c: { id: number }) => c.id);
        const fromSubs = Array.from(
          new Set(
            (only.subcategories || [])
              .map((s: { category_id?: number }) => s.category_id)
              .filter((id): id is number => typeof id === 'number' && id > 0)
          )
        );
        const favorites = (all.categories || [])
          .filter((c) => Number(c.favorite) === 1)
          .map((c) => c.id);
        const merged = Array.from(new Set([...fromOnly, ...fromSubs]));
        setPickerSelectedIds(merged.length > 0 ? merged : favorites);
      })
      .catch(() => {
        if (!cancelled) setPickerError(t('homeAddCategoryLoadFailed'));
      })
      .finally(() => {
        if (!cancelled) setPickerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [manageCategoriesOpen, user?.token, t]);

  const resolveSelectedSubcategoryIds = useCallback((): number[] | undefined => {
    if (!selectedCategoryId) return undefined;
    const selectedCategory = categories.find((cat) => cat.id === selectedCategoryId);
    if (!selectedCategory?.subcategories?.length) return undefined;
    const ids = selectedCategory.subcategories
      .filter((sub) => !deselectedSubcategoryIds.has(parseInt(sub.id, 10)))
      .map((sub) => parseInt(sub.id, 10));
    return ids.length > 0 ? ids : undefined;
  }, [selectedCategoryId, categories, deselectedSubcategoryIds]);

  // Fetch first page when category/subcategory selection changes
  useEffect(() => {
    if (!user?.token || categories.length === 0) return;

    const fetchBusinesses = async () => {
      setIsLoadingBusinesses(true);
      isLoadingBusinessesRef.current = true;
      setFeedPage(1);
      setFeedHasMore(false);
      feedHasMoreRef.current = false;
      try {
        const resolved = resolveViewerCoordinates(locationRef.current);
        if (favoritesOnly) {
          const lat = parseFloat(resolved.lat);
          const lng = parseFloat(resolved.lng);
          const data = await fetchFavorites(user.token, lat, lng, getApiLanguage(), user.id);
          const list = data.businesses || [];
          setBusinesses(list);
          businessesLenRef.current = list.length;
          setVisibleFeedCount(HOME_FEED_PAGE_SIZE);
          visibleFeedCountRef.current = HOME_FEED_PAGE_SIZE;
          setFeedHasMore(false);
          feedHasMoreRef.current = false;
          return;
        }
        const { lat, lng } = resolved;
        const placeType = placeTypeFromMode(locationMode);
        const selectedSubcategoryIds = resolveSelectedSubcategoryIds();

        console.log(`[API Call] Fetching businesses page=1 limit=${HOME_FEED_PAGE_SIZE}`, selectedSubcategoryIds);
        const data = await fetchUserCategories(
          getApiLanguage(),
          user.token,
          lat,
          lng,
          placeType,
          selectedSubcategoryIds,
          { page: 1, limit: HOME_FEED_PAGE_SIZE }
        );

        if (data.message && !data.businesses) {
          console.warn('[Businesses Fetch] API returned error message:', data.message);
          setBusinesses([]);
          return;
        }

        if (data.businesses && Array.isArray(data.businesses)) {
          setBusinesses(data.businesses);
          businessesLenRef.current = data.businesses.length;
          setFeedPage(1);
          setVisibleFeedCount(HOME_FEED_PAGE_SIZE);
          visibleFeedCountRef.current = HOME_FEED_PAGE_SIZE;
          const more = Boolean(data.has_more);
          setFeedHasMore(more);
          feedHasMoreRef.current = more;
          console.log(`[API Response] Loaded ${data.businesses.length} businesses (has_more=${more})`);
        } else {
          console.log('[API Response] No businesses array in API response');
          setBusinesses([]);
          businessesLenRef.current = 0;
        }
      } catch (error) {
        console.error('Error fetching businesses:', error);
        setBusinesses([]);
        businessesLenRef.current = 0;
      } finally {
        setIsLoadingBusinesses(false);
        isLoadingBusinessesRef.current = false;
      }
    };

    void fetchBusinesses();
  }, [user?.token, user?.id, selectedCategoryId, deselectedSubcategoryIds, categories, langTick, favoritesOnly, locationMode, resolveSelectedSubcategoryIds]);

  const fetchNextFeedPage = async () => {
    if (!user?.token || favoritesOnly) return;
    if (!feedHasMoreRef.current || isLoadingMoreRef.current || isLoadingBusinessesRef.current) return;

    const nextPage = feedPage + 1;
    isLoadingMoreRef.current = true;
    setIsLoadingMoreBusinesses(true);
    try {
      const resolved = resolveViewerCoordinates(locationRef.current);
      const placeType = placeTypeFromMode(locationMode);
      const selectedSubcategoryIds = resolveSelectedSubcategoryIds();
      const data = await fetchUserCategories(
        getApiLanguage(),
        user.token,
        resolved.lat,
        resolved.lng,
        placeType,
        selectedSubcategoryIds,
        { page: nextPage, limit: HOME_FEED_PAGE_SIZE }
      );

      if (data.businesses && Array.isArray(data.businesses) && data.businesses.length > 0) {
        setBusinesses((prev) => {
          const seen = new Set(prev.map((b) => b.id));
          const appended = data.businesses!.filter((b) => !seen.has(b.id));
          const next = appended.length ? [...prev, ...appended] : prev;
          businessesLenRef.current = next.length;
          return next;
        });
        setFeedPage(nextPage);
        setVisibleFeedCount((c) => {
          const next = c + HOME_FEED_PAGE_SIZE;
          visibleFeedCountRef.current = next;
          return next;
        });
      }

      const more = Boolean(data.has_more);
      setFeedHasMore(more);
      feedHasMoreRef.current = more;
    } catch (error) {
      console.error('Error loading more businesses:', error);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMoreBusinesses(false);
    }
  };

  /** Reveal next 15 from the in-memory list, or fetch the next API page when needed. */
  const loadMoreBusinesses = async () => {
    if (isLoadingMoreRef.current || isLoadingBusinessesRef.current) return;

    const buffered = businessesLenRef.current;
    const shown = visibleFeedCountRef.current;
    if (shown < buffered) {
      // Hold skeleton + spinning refresh long enough to read, then swap in cards.
      isLoadingMoreRef.current = true;
      setIsLoadingMoreBusinesses(true);
      window.setTimeout(() => {
        setVisibleFeedCount((c) => {
          const next = Math.min(c + HOME_FEED_PAGE_SIZE, businessesLenRef.current);
          visibleFeedCountRef.current = next;
          return next;
        });
        isLoadingMoreRef.current = false;
        setIsLoadingMoreBusinesses(false);
      }, 520);
      return;
    }

    await fetchNextFeedPage();
  };

  useEffect(() => {
    businessesLenRef.current = businesses.length;
  }, [businesses.length]);

  useEffect(() => {
    const node = loadMoreSentinelRef.current;
    if (!node || feedTab !== 'feed') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          void loadMoreBusinesses();
        }
      },
      { root: null, rootMargin: '280px 0px', threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // Intentionally re-bind when feed page / hasMore / loading flags change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedTab, favoritesOnly, feedPage, feedHasMore, isLoadingMoreBusinesses, isLoadingBusinesses, businesses.length, visibleFeedCount, user?.token, selectedCategoryId, deselectedSubcategoryIds, locationMode]);

  // Handle category selection (matches iOS: clicking same category toggles subcategories off)
  const handleCategorySelect = (categoryId: string | null, subcategoryId?: string | null, subsubcategoryId?: string | null) => {
    // If clicking the same category, deselect it (hide subcategories)
    if (categoryId === selectedCategoryId) {
      setSelectedCategoryId(null);
      setSelectedSubcategoryId(null);
      setSelectedSubsubcategoryId(null);
      setDeselectedSubcategoryIds(new Set()); // Reset deselected subcategories
    } else {
      // Select new category - show its subcategories, all selected by default
      setSelectedCategoryId(categoryId);
      setSelectedSubcategoryId(subcategoryId || null);
      setSelectedSubsubcategoryId(subsubcategoryId || null);
      setDeselectedSubcategoryIds(new Set()); // Reset - all subcategories selected by default
    }
    
    setLlmFilteredProposalIds(null); 
    setActiveLlmQuery(null);
    setChatError(null); 
    setChatQuery(''); // Also clear search input on category change
  };
  
  const handleSaveManagedCategories = async (selectedIds: number[]) => {
    if (!user?.token) return;
    setPickerBusy(true);
    setPickerError(null);
    try {
      await updateUserCategories(selectedIds, getApiLanguage(), user.token);
      setManageCategoriesOpen(false);
      setSelectedCategoryId(null);
      setSelectedSubcategoryId(null);
      setSelectedSubsubcategoryId(null);
      setDeselectedSubcategoryIds(new Set());
      setLangTick((n) => n + 1);
    } catch (e) {
      setPickerError(e instanceof Error ? e.message : t('homeManageCategoriesFailed'));
    } finally {
      setPickerBusy(false);
    }
  };

  // iOS: subcategory tap is exclusive — one active, or reset to all selected.
  const handleSubcategoryToggle = (subcategoryId: string) => {
    const subId = parseInt(subcategoryId, 10);
    const selectedCategory = categories.find((cat) => cat.id === selectedCategoryId);
    if (!selectedCategory?.subcategories?.length) return;

    const allSubcategoryIds = selectedCategory.subcategories.map((sub) => parseInt(sub.id, 10));
    const selectedIds = allSubcategoryIds.filter((id) => !deselectedSubcategoryIds.has(id));
    const isExclusiveSelection = selectedIds.length === 1 && selectedIds[0] === subId;

    if (isExclusiveSelection) {
      setDeselectedSubcategoryIds(new Set());
      setSelectedSubcategoryId(null);
    } else {
      setDeselectedSubcategoryIds(new Set(allSubcategoryIds.filter((id) => id !== subId)));
      setSelectedSubcategoryId(String(subId));
    }
    setSelectedSubsubcategoryId(null);

    setLlmFilteredProposalIds(null);
    setActiveLlmQuery(null);
    setChatError(null);
  };

  // Function to reset LLM search results and clear input
  const resetLlmFilter = () => {
    setLlmFilteredProposalIds(null);
    setActiveLlmQuery(null);
    setChatError(null);
    setChatQuery(''); // Clear the input field as well
  };

  // Handler for input changes from Header (kept for Header wiring)
  const _handleQueryChange = (newValue: string) => {
    setChatQuery(newValue);
  };
  void _handleQueryChange;

  // Transform businesses from API to Proposal format for display
  const transformBusinessToProposal = (business: ApiBusiness) => {
    const businessName = business.name || ((business as any).main_business?.business?.name) || 'Unknown Business';
    const categoryName = business.category?.name || business.subcategory?.name || '';
    const logoUrl = getBusinessLogoUrl(business.logo) || null;
    const username = business.username?.replace('@', '') || business.id.toString();

    let coverUrl: string | null = null;
    if (business.coverStrings) {
      const first = business.coverStrings.split(',').map((s) => s.trim()).filter(Boolean)[0];
      if (first) coverUrl = getBusinessCoverUrl(first);
    }
    if (!coverUrl && business.covers?.[0]?.cover) {
      coverUrl = getBusinessCoverUrl(business.covers[0].cover);
    }

    let location = formatDistanceKm(business.distance) ?? '';
    if (!location && business.addresses && business.addresses.length > 0) {
      const address = business.addresses[0];
      location = address.city || address.address || address.country || '';
    }

    return {
      id: business.id.toString(),
      title: businessName,
      description: business.description || '',
      budget: 0,
      deadline: 'Flexible',
      category: categoryName,
      user: {
        id: business.id.toString(),
        name: businessName,
        username: business.username || `@${business.id}`,
        avatar: logoUrl,
        rating: 4.5,
      },
      tags: [] as string[],
      location,
      coverUrl,
      favorite: Number((business as { favorite?: number | boolean }).favorite) === 1,
      postedAt: business.created_at ? (() => {
        try {
          const date = new Date(business.created_at);
          return isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
        } catch {
          return new Date().toISOString();
        }
      })() : new Date().toISOString(),
      username,
    };
  };

  const markBusinessFavorite = useCallback((businessId: number, favorite: boolean) => {
    setBusinesses((prev) =>
      prev.map((b) => (b.id === businessId ? { ...b, favorite: favorite ? 1 : 0 } : b))
    );
  }, []);

  const handleFeedSaveToList = useCallback(async (proposal: FeedProposal) => {
    const businessId = parseInt(proposal.id, 10);
    if (!Number.isFinite(businessId)) return;

    if (!user?.token) {
      navigate('/login');
      return;
    }

    if (proposal.favorite) {
      setSaveActionBusy(true);
      try {
        await removeBusinessFromAllFolders(businessId, user.token, getApiLanguage());
        markBusinessFavorite(businessId, false);
      } catch (e) {
        console.error('Error removing bookmark:', e);
      } finally {
        setSaveActionBusy(false);
      }
      return;
    }

    setSaveTargetId(businessId);
    setSaveFolderErr(null);
    setSaveFolderOk(null);
    try {
      const res = await fetchUserFolders(user.token, getApiLanguage());
      setSaveFolders(res.folders || []);
      setSaveFolderOpen(true);
    } catch (e) {
      setSaveFolderErr(e instanceof Error ? e.message : 'Could not load lists');
      setSaveFolderOpen(true);
    }
  }, [markBusinessFavorite, navigate, user?.token]);

  const handleChatSubmitRef = useRef<(q: string) => Promise<void>>(async () => {});

  const handleChatSubmit = async (queryToSubmit: string) => {
    const trimmedQuery = queryToSubmit.trim();
    if (!groq || !trimmedQuery) {
      if (!trimmedQuery) {
        setIsChatLoading(false);
      }
      return;
    }
    if (trimmedQuery === activeLlmQuery && llmFilteredProposalIds !== null) {
      return;
    }

    setIsChatLoading(true);
    setChatError(null);
    setSelectedCategoryId(null);

    const allProposalsForLLM = businesses.map(transformBusinessToProposal);

    if (allProposalsForLLM.length === 0) {
      console.warn('[LLM Filter] No businesses available for LLM filtering');
      setChatError(t('homeAiNoBusinesses'));
      setIsChatLoading(false);
      return;
    }

    const proposalContext = allProposalsForLLM
      .map(
        (p) =>
          `ID: ${p.id}\nTitle: ${p.title}\nCategory: ${p.category}\nDescription: ${p.description}\nBudget: ${p.budget}\nLocation: ${p.location}\n---`
      )
      .join('\n');

    const systemPrompt = `You are an AI assistant helping users find relevant service proposals based on their query. You will be given a list of proposals with their ID, Title, Category, Description, Budget, and Location.

Based on the user's query, identify the most relevant proposals from the list provided.
Consider the category, budget constraints, location preferences, and keywords in the description.

Respond ONLY with a comma-separated list of the IDs of the matching proposals. For example: "3,12,21".
If no proposals match the query, respond with an empty string.

Available Proposals:\n${proposalContext}`;

    try {
      const chatCompletion = await groq.chat.completions.create({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: trimmedQuery },
        ],
        model: 'llama-3.1-8b-instant',
        temperature: 0.2,
      });

      const responseContent = chatCompletion.choices[0]?.message?.content || '';
      const ids = responseContent
        .split(/[,\s]+/)
        .map((id) => id.trim().replace(/[^0-9]/g, ''))
        .filter((id) => id !== '' && !isNaN(Number(id)));

      setLlmFilteredProposalIds(ids);
      setActiveLlmQuery(trimmedQuery);
      setChatError(null);
    } catch (error) {
      console.error('[HomePage handleChatSubmit] Error calling Groq API:', error);
      setChatError(t('homeAiFailed'));
      setLlmFilteredProposalIds([]);
      setActiveLlmQuery(trimmedQuery);
    } finally {
      setIsChatLoading(false);
    }
  };

  handleChatSubmitRef.current = handleChatSubmit;

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (chatQuery.trim()) {
      debounceTimeoutRef.current = setTimeout(() => {
        void handleChatSubmitRef.current(chatQuery);
      }, DEBOUNCE_DELAY);
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [chatQuery]);

  // Determine which proposals to display
  // Note: Backend filters businesses by subcategory_id, so we just display all businesses returned
  const allProposals = businesses && businesses.length > 0 
    ? businesses.map(transformBusinessToProposal)
    : [];
  const realProposals = allProposals.filter((p) => !isTestEntityName(p.title));
  let displayProposals =
    realProposals.length >= 2 ? realProposals : allProposals;
  
  // Only apply LLM filtering if active (backend handles category/subcategory filtering)
  if (llmFilteredProposalIds !== null) {
    displayProposals = allProposals.filter(p => llmFilteredProposalIds.includes(p.id));
  } else {
    // Progressive fill: show 15 at a time, reveal more on scroll.
    displayProposals = displayProposals.slice(0, visibleFeedCount);
  }

  const canLoadMoreFeed =
    llmFilteredProposalIds === null &&
    (visibleFeedCount < businesses.length || (!favoritesOnly && feedHasMore));

  const _triggerImmediateSubmit = () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    handleChatSubmit(chatQuery);
  };
  void _triggerImmediateSubmit;

  return (
    <AppShell hideTitle maxWidth="feed" showCompanion>
          {(isLoadingCategories || isLoadingBusinesses) && (
            <LinearProgress sx={{ position: 'sticky', top: 0, zIndex: 2, mb: 0 }} />
          )}

          {/* Substack order: composer → tabs → category pills → feed */}
          {feedTab === 'feed' && (
            <Box
              onClick={() => navigate('/visi/create')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.75,
                py: 1.35,
                mb: 2,
                borderRadius: DesignSystem.borderRadius.pill,
                bgcolor: DesignSystem.colors.surface,
                cursor: 'pointer',
                '&:hover': { bgcolor: DesignSystem.colors.surfaceElevated },
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: DesignSystem.colors.surfaceElevated,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 15, fontWeight: 500, color: DesignSystem.colors.textSecondary }}>
                {t('homeComposer')}
              </Typography>
            </Box>
          )}

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 0,
            mb: 1.5,
          }}>
            <Tabs
              value={feedTab}
              onChange={(_e, newValue) => {
                setFeedTab(newValue);
              }}
              sx={{
                minHeight: 'auto',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 15,
                  color: DesignSystem.colors.textSecondary,
                  minHeight: 40,
                  px: 0.5,
                  mr: 2,
                  '&.Mui-selected': {
                    color: DesignSystem.colors.textPrimary,
                  },
                },
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
              }}
            >
              <Tab value="feed" label={t('homeTabForYou')} />
              <Tab
                value="notifications"
                label={t('homeTabNotifications')}
                icon={
                  notificationCount > 0 ? (
                    <Badge
                      badgeContent={notificationCount}
                      max={99}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: DesignSystem.colors.action,
                          color: DesignSystem.colors.onPrimary,
                          fontSize: '10px',
                          minWidth: '18px',
                          height: '18px',
                          position: 'relative',
                          transform: 'none',
                        },
                      }}
                    />
                  ) : undefined
                }
                iconPosition="end"
              />
            </Tabs>
            <IconButton
              size="small"
              aria-label={t('homeFeedFilters')}
              aria-pressed={feedFiltersActive}
              onClick={() => setFilterOpen(true)}
              sx={{
                color: feedFiltersActive
                  ? DesignSystem.colors.primary
                  : DesignSystem.colors.textSecondary,
                '&:hover': { color: DesignSystem.colors.textPrimary, backgroundColor: DesignSystem.colors.hoverStrong },
              }}
            >
              <FilterList />
            </IconButton>
          </Box>

          {feedTab === 'feed' && (
            <Box sx={{ mb: 2.5, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
              <CategoryList
                categories={categories}
                onSelectAll={() => handleCategorySelect(null, null, null)}
                onDeselectAll={() => handleCategorySelect(null, null, null)}
                onAddCategory={() => setManageCategoriesOpen(true)}
                addCategoryAriaLabel={t('homeManageCategoriesAria')}
                onSelect={handleCategorySelect}
                onSubcategoryToggle={handleSubcategoryToggle}
                selectedCategoryId={selectedCategoryId}
                selectedSubcategoryId={selectedSubcategoryId}
                selectedSubsubcategoryId={selectedSubsubcategoryId}
                deselectedSubcategoryIds={deselectedSubcategoryIds}
              />
            </Box>
          )}

           {/* Active LLM Query Chip unchanged, uses resetLlmFilter from this scope */}
          {activeLlmQuery && !isChatLoading && (
             <Box sx={{ display: 'flex', justifyContent: 'center', mt: DesignSystem.spacing.lg, mb: DesignSystem.spacing.md }}>
              <Chip
                label={t('homeAiResultsFor', { q: activeLlmQuery })}
                onDelete={resetLlmFilter} 
                deleteIcon={<Close />} 
                variant="filled" 
                sx={{ 
                  maxWidth: '85%', 
                  height: 'auto', 
                  p: 1.8, 
                  fontSize: DesignSystem.typography.body.fontSize, 
                  borderRadius: DesignSystem.borderRadius.pill, 
                  backgroundColor: DesignSystem.colors.action,
                  color: DesignSystem.colors.onPrimary,
                  '.MuiChip-label': { 
                    overflowWrap: 'break-word', 
                    whiteSpace: 'normal', 
                    textAlign: 'left', 
                    lineHeight: 1.45, 
                    paddingRight: DesignSystem.spacing.md,
                  }, 
                  '.MuiChip-deleteIcon': { 
                    fontSize: DesignSystem.typography.h3.fontSize,
                    color: DesignSystem.colors.onPrimary,
                  } 
                }}
              />
            </Box>
          )}

           {/* Loading/Error/No Results Messages unchanged */}
           {isChatLoading && (
             <Typography sx={{
               textAlign: 'center', 
               my: DesignSystem.spacing.lg,
               color: DesignSystem.colors.textSecondary,
               fontSize: DesignSystem.typography.body.fontSize,
             }}>
               {t('homeAiAsking', { q: chatQuery || '…' })}
             </Typography>
           )}
           {chatError && (
             <Typography sx={{
               textAlign: 'center', 
               my: DesignSystem.spacing.lg,
               color: DesignSystem.colors.error,
               fontSize: DesignSystem.typography.body.fontSize,
             }}>
               {chatError}
             </Typography>
           )}
           {activeLlmQuery && llmFilteredProposalIds?.length === 0 && !isChatLoading && !chatError && (
             <Typography sx={{
               textAlign: 'center', 
               my: DesignSystem.spacing.lg,
               color: DesignSystem.colors.textSecondary,
               fontSize: DesignSystem.typography.body.fontSize,
             }}>
               {t('homeAiNoMatch', { q: activeLlmQuery })}
             </Typography>
           )}

           {/* Main content area - shows Feed or Notifications */}
          {feedTab === 'feed' ? (
            <Box sx={{ mt: 0.5 }}>
              {isLoadingBusinesses && displayProposals.length === 0 ? (
                <FeedSkeleton count={8} />
              ) : displayProposals.length === 0 ? (
                <Box sx={{ py: 8, textAlign: 'center', color: DesignSystem.colors.textSecondary }}>
                  <Typography sx={{ fontWeight: 600, mb: 0.75, color: DesignSystem.colors.textPrimary }}>
                    {t('homeFeedEmptyTitle')}
                  </Typography>
                  <Typography sx={{ fontSize: 14 }}>
                    {t('homeFeedEmptyBody')}
                  </Typography>
                </Box>
              ) : (
                <>
                  <ProposalList
                    key={`feed-${selectedCategoryId || 'all'}-${favoritesOnly ? 'fav' : 'all'}-${langTick}`}
                    proposals={displayProposals}
                    onSaveToList={(proposal) => void handleFeedSaveToList(proposal)}
                  />
                  {canLoadMoreFeed && (
                    <FeedLoadMoreFooter
                      ref={loadMoreSentinelRef}
                      loading={isLoadingMoreBusinesses}
                      skeletonCount={4}
                    />
                  )}
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              {newsLoading && (
                <Typography sx={{ fontSize: 14, color: DesignSystem.colors.textSecondary, py: 4, textAlign: 'center' }}>
                  {t('homeUpdatesLoading')}
                </Typography>
              )}
              {newsErr && (
                <Typography sx={{ fontSize: 14, color: DesignSystem.colors.error, py: 2 }}>{newsErr}</Typography>
              )}
              {!newsLoading && !newsErr && newsItems.length === 0 && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 600, mb: 0.75, color: DesignSystem.colors.textPrimary }}>
                    {t('homeUpdatesEmptyTitle')}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: DesignSystem.colors.textSecondary }}>
                    {t('homeUpdatesEmptyBody')}
                  </Typography>
                </Box>
              )}
              {!newsLoading &&
                newsItems.map((n, i) => {
                  const b = n.business as ApiBusiness | undefined;
                  const msg = typeof n.message === 'string' ? n.message : 'Update';
                  const time = typeof n.time === 'string' ? n.time : '';
                  const handle = b?.username?.replace(/^@/, '') || (b ? String(b.id) : '');
                  return (
                    <Box
                      key={i}
                      component="article"
                      onClick={() => handle && navigate(`/${handle}`)}
                      sx={{
                        cursor: handle ? 'pointer' : 'default',
                        py: 1.75,
                        borderBottom: `1px solid ${DesignSystem.colors.border}`,
                        '&:hover': handle ? { backgroundColor: DesignSystem.colors.hover } : undefined,
                      }}
                    >
                      <Typography sx={{ fontSize: 15, fontWeight: 600, color: DesignSystem.colors.textPrimary }}>
                        {msg}
                      </Typography>
                      {b?.name && (
                        <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, mt: 0.35 }}>
                          {b.name}
                        </Typography>
                      )}
                      {time && (
                        <Typography sx={{ fontSize: 12, color: DesignSystem.colors.textTertiary, mt: 0.5 }}>
                          {time}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
            </Box>
          )}

      <CategoryPickerModal
        open={manageCategoriesOpen}
        loading={pickerLoading}
        saving={pickerBusy}
        error={pickerError}
        categories={pickerCategories}
        initiallySelectedIds={pickerSelectedIds}
        onClose={() => setManageCategoriesOpen(false)}
        onSave={(ids) => void handleSaveManagedCategories(ids)}
      />

      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('feedFilter')}</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Switch
                checked={favoritesOnly}
                onChange={(_, c) => {
                  setFavoritesOnly(c);
                }}
              />
            }
            label={t('favoritesOnly')}
            sx={{ mb: 2, alignItems: 'flex-start', mt: 0.5 }}
          />
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <FormLabel component="legend" sx={{ fontSize: 13, mb: 1 }}>
              {t('locationFilter')}
            </FormLabel>
            <RadioGroup
              value={locationMode}
              onChange={(_, v) => {
                const mode = v as LocationMode;
                setLocationMode(mode);
                try {
                  localStorage.setItem(LOCATION_MODE_KEY, mode);
                } catch {
                  /* ignore */
                }
              }}
            >
              <FormControlLabel
                value="all"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography sx={{ fontSize: 14 }}>{t('filterAll')}</Typography>
                    <Typography sx={{ fontSize: 12, color: DesignSystem.colors.textSecondary }}>
                      {t('filterAllHint')}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="fixed"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography sx={{ fontSize: 14 }}>{t('filterFixedAddress')}</Typography>
                    <Typography sx={{ fontSize: 12, color: DesignSystem.colors.textSecondary }}>
                      {t('filterFixedHint')}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="gps"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography sx={{ fontSize: 14 }}>{t('filterGpsBased')}</Typography>
                    <Typography sx={{ fontSize: 12, color: DesignSystem.colors.textSecondary }}>
                      {t('filterGpsHint')}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterOpen(false)}>{t('done')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={saveFolderOpen} onClose={() => setSaveFolderOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('addToList')}</DialogTitle>
        <DialogContent>
          {saveFolderErr && <Alert severity="error" sx={{ mb: 1 }}>{saveFolderErr}</Alert>}
          {saveFolderOk && <Alert severity="success" sx={{ mb: 1 }}>{saveFolderOk}</Alert>}
          {saveFolders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('folderNoListsYet')}
            </Typography>
          ) : (
            <List>
              {saveFolders.map((f, i) => (
                <ListItemButton
                  key={f.id}
                  disabled={saveActionBusy || saveTargetId == null}
                  onClick={async () => {
                    if (!user?.token || saveTargetId == null) return;
                    setSaveActionBusy(true);
                    setSaveFolderErr(null);
                    try {
                      await addBusinessToFolder(saveTargetId, f.id, user.token, getApiLanguage());
                      markBusinessFavorite(saveTargetId, true);
                      setSaveFolderOk(t('addedToList', { name: displayFolderName(f.name, i) }));
                      setTimeout(() => setSaveFolderOpen(false), 900);
                    } catch (e) {
                      setSaveFolderErr(e instanceof Error ? e.message : t('couldNotAdd'));
                    } finally {
                      setSaveActionBusy(false);
                    }
                  }}
                >
                  <ListItemText primary={displayFolderName(f.name, i)} secondary={f.business_count != null ? t('listItemsCount', { n: f.business_count }) : undefined} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveFolderOpen(false)}>{t('close')}</Button>
          <Button onClick={() => { setSaveFolderOpen(false); navigate('/bookmarks'); }}>{t('manageLists')}</Button>
        </DialogActions>
      </Dialog>

    </AppShell>
  );
} 